import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { ArrowLeft, AlertTriangle, CheckCircle2, Copy, Loader2, MailWarning, Search, X } from 'lucide-react';
import { toast } from "react-toastify";
import useApi from "@src/api_service/useApi";
import { cn } from "@src/utils/app_functions";
import {
  ApiErrorBody,
  BillingCycle,
  CreatedSchoolPaymentLink,
  LinkCurrency,
  LinkType,
  SchoolPlanOption,
} from "./interface";
import { authHeaders, copyToClipboard, errorMessage, fetchSchoolPlans, formatDate, formatMoney, LINK_TYPE_LABELS } from "./utils";
import { useLinkActions } from "./components/LinkActions";

type LayoutContextType = {
  setSchoolAdminPageName: React.Dispatch<React.SetStateAction<string>>;
  setShowInput: React.Dispatch<React.SetStateAction<boolean>>;
  setMenu: React.Dispatch<React.SetStateAction<React.ReactElement | undefined>>;
  setIsAffiliate: React.Dispatch<React.SetStateAction<boolean>>;
};

interface SchoolOption {
  _id?: string;
  appID?: string;
  organizationName?: string;
  name?: string;
  email?: string;
  status?: string;
}

interface FormState {
  linkType: LinkType;
  planId: string;
  numberOfStudents: string;
  billingCycle: BillingCycle;
  amount: string;
  currency: LinkCurrency;
  email: string;
  note: string;
  emailSubject: string;
  emailMessage: string;
}

type FormErrors = Partial<Record<keyof FormState | 'school', string>>;

const BILLING_CYCLES: BillingCycle[] = ['monthly', 'quarterly', 'yearly'];

const inputClass = (hasError?: boolean) => cn(
  "w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#E55109] focus:border-transparent outline-none text-sm",
  hasError ? "border-red-400" : "border-gray-300"
);

const CreateSchoolPaymentLinkPage: React.FC = () => {
  const navigate = useNavigate();
  const path = window.location.pathname;
  const [searchParams] = useSearchParams();
  const { setSchoolAdminPageName, setShowInput, setMenu, setIsAffiliate } = useOutletContext<LayoutContextType>();

  const prefilledAppId = searchParams.get('appId');
  const [school, setSchool] = useState<SchoolOption | null>(
    prefilledAppId
      ? { appID: prefilledAppId, organizationName: searchParams.get('name') || undefined, email: searchParams.get('email') || undefined }
      : null
  );
  const [schoolQuery, setSchoolQuery] = useState('');
  const [schoolResults, setSchoolResults] = useState<SchoolOption[]>([]);
  const [searchingSchools, setSearchingSchools] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();

  const [plans, setPlans] = useState<SchoolPlanOption[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);

  const [form, setForm] = useState<FormState>({
    linkType: 'subscription',
    planId: '',
    numberOfStudents: '',
    billingCycle: 'yearly',
    amount: '',
    currency: 'NGN',
    email: '',
    note: '',
    emailSubject: '',
    emailMessage: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  // The amount follows the plan's standard price until the admin types their own negotiated figure.
  const [amountEdited, setAmountEdited] = useState(false);
  const [created, setCreated] = useState<CreatedSchoolPaymentLink | null>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);

  // Wraps the selected message text in **...** (bold in the email), or inserts **bold text** at the cursor.
  const boldSelection = () => {
    const textarea = messageRef.current;
    if (!textarea) return;
    const { selectionStart: start, selectionEnd: end, value } = textarea;
    const selected = value.slice(start, end) || 'bold text';
    const next = `${value.slice(0, start)}**${selected}**${value.slice(end)}`;
    setField('emailMessage', next.slice(0, 5000));
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(start + 2, start + 2 + selected.length);
    });
  };

  const { loading, post } = useApi();
  const { get: searchGet } = useApi();
  const { get: feeGet } = useApi();
  // The per-student onboarding rate (backend: SCHOOL_ONBOARDING_FEE_PER_STUDENT, default ₦1,000).
  const [onboardingFee, setOnboardingFee] = useState<{ perStudent: number; currency: LinkCurrency } | null>(null);
  const resendActions = useLinkActions(() => {});

  useEffect(() => {
    setSchoolAdminPageName("Create Payment Link");
    setShowInput(false);
    setMenu(undefined);
    setIsAffiliate(false);
  }, [path]);

  useEffect(() => {
    feeGet<{ data: { perStudent: number; currency: LinkCurrency } }, ApiErrorBody>(
      '/third_party/school_onboarding_fee',
      authHeaders(false),
      { loading: false, onSuccess: (res) => setOnboardingFee(res.data ?? null), onError: () => setOnboardingFee(null) }
    );
  }, []);

  useEffect(() => {
    fetchSchoolPlans()
      .then(setPlans)
      .catch(() => toast.error('Could not load school plans'))
      .finally(() => setPlansLoading(false));
  }, []);

  const searchSchools = (query: string) => {
    setSchoolQuery(query);
    setShowResults(true);
    clearTimeout(searchTimer.current);
    if (!query.trim()) {
      setSchoolResults([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      setSearchingSchools(true);
      await searchGet<{ data: { apps: SchoolOption[] } }, ApiErrorBody>(
        `/third_party/get_all_apps?page=1&limit=8&search=${encodeURIComponent(query.trim())}`,
        authHeaders(false),
        {
          loading: false,
          onSuccess: (res) => setSchoolResults(Array.isArray(res.data?.apps) ? res.data.apps : []),
          onError: () => setSchoolResults([]),
        }
      );
      setSearchingSchools(false);
    }, 350);
  };

  const onboarding = form.linkType === 'onboarding';
  const selectedPlan = useMemo(() => plans.find((p) => p._id === form.planId), [plans, form.planId]);
  const students = Number(form.numberOfStudents);

  const outsideRange = Boolean(
    !onboarding && selectedPlan && students > 0 &&
    (students < selectedPlan.minStudents || (selectedPlan.maxStudents != null && students > selectedPlan.maxStudents))
  );

  // What the school would pay through the normal checkout, for comparison with the negotiated amount.
  // Plan prices come back in one currency only (picked by region), so it is only shown when it matches.
  const referencePrice = useMemo(() => {
    if (onboarding || !selectedPlan || !(students > 0) || selectedPlan.currency !== form.currency) return null;
    const discount =
      form.billingCycle === 'yearly' ? selectedPlan.yearlyDiscountRate ?? 0 :
      form.billingCycle === 'quarterly' ? selectedPlan.quarterlyDiscountRate ?? 0 : 0;
    const perStudent = selectedPlan.price - (selectedPlan.price * discount) / 100;
    return Math.round(perStudent * students * 100) / 100;
  }, [onboarding, selectedPlan, students, form.currency, form.billingCycle]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  useEffect(() => {
    if (amountEdited || referencePrice === null) return;
    setField('amount', String(referencePrice));
  }, [referencePrice, amountEdited]);

  // Fill what the plan determines: its currency, and a student count inside its range
  // (an existing count is kept when it already fits).
  const selectPlan = (planId: string) => {
    const plan = plans.find((p) => p._id === planId);
    setForm((prev) => {
      if (!plan) return { ...prev, planId };
      const count = Number(prev.numberOfStudents);
      const fits = count >= plan.minStudents && (plan.maxStudents == null || count <= plan.maxStudents);
      return {
        ...prev,
        planId,
        currency: plan.currency,
        numberOfStudents: fits ? prev.numberOfStudents : String(plan.minStudents),
      };
    });
    setErrors((prev) => ({ ...prev, planId: undefined, numberOfStudents: undefined, currency: undefined }));
    setAmountEdited(false);
  };

  const validate = () => {
    const next: FormErrors = {};
    if (!school?.appID) next.school = 'Select a school';
    if (!onboarding && !form.planId) next.planId = 'Select a plan';
    if (!Number.isInteger(students) || students < 1) next.numberOfStudents = 'Enter a whole number of at least 1';
    // An onboarding fee's amount is calculated by the backend from the per-student rate.
    if (!onboarding) {
      const amount = Number(form.amount);
      if (!(amount > 0)) next.amount = 'Amount must be greater than 0';
      else if (!/^\d+(\.\d{1,2})?$/.test(form.amount.trim())) next.amount = 'Use at most 2 decimal places';
    }
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) next.email = 'Invalid email address';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !school?.appID) return;

    const payload = {
      appId: school.appID,
      linkType: form.linkType,
      numberOfStudents: students,
      // An onboarding fee has no plan, and its amount/currency come from the per-student rate.
      ...(onboarding
        ? {}
        : { planId: form.planId, billingCycle: form.billingCycle, amount: Number(form.amount), currency: form.currency }),
      ...(form.email.trim() ? { email: form.email.trim() } : {}),
      ...(form.note.trim() ? { note: form.note.trim() } : {}),
      ...(form.emailSubject.trim() ? { emailSubject: form.emailSubject.trim() } : {}),
      ...(form.emailMessage.trim() ? { emailMessage: form.emailMessage.trim() } : {}),
    };

    await post<{ message?: string; data: CreatedSchoolPaymentLink }, ApiErrorBody>(
      '/third_party/school_payment_links',
      payload as unknown as FormData,
      authHeaders(),
      {
        onSuccess: (res) => {
          setCreated(res.data);
          if (res.data?.emailSent) toast.success(res.message || 'Payment link created and sent to the school');
          else toast.warning(res.message || 'Payment link created, but the email could not be sent');
        },
        onError: (err) => toast.error(errorMessage(err, 'Could not create the payment link')),
      }
    );
  };

  const resetForm = () => {
    setCreated(null);
    setForm((prev) => ({ ...prev, note: '', emailSubject: '', emailMessage: '' }));
    setAmountEdited(false);
  };

  if (created) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-md p-8 space-y-6">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-8 h-8 text-emerald-600 shrink-0" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Payment link created</h1>
              <p className="text-gray-600 mt-1">
                {formatMoney(created.amount, created.currency)}
                {created.linkType === 'onboarding' ? ` onboarding fee (${created.numberOfStudents} student slots)` : ''} for{' '}
                {created.organizationName || created.thirdPartyAppId}, payable until {formatDate(created.expiresAt)}.
              </p>
            </div>
          </div>

          {created.emailSent ? (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800">
              Emailed to <span className="font-medium">{created.recipientEmail}</span>.
            </div>
          ) : (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-800 space-y-2">
              <div className="flex items-center gap-2 font-medium">
                <MailWarning className="w-4 h-4" /> The email to {created.recipientEmail} could not be sent.
              </div>
              {created.emailError && <div className="text-red-700">{created.emailError}</div>}
              <button
                onClick={async () => {
                  if (await resendActions.resend(created)) setCreated({ ...created, emailSent: true, emailError: undefined });
                }}
                disabled={resendActions.loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60 font-medium"
              >
                {resendActions.loading ? 'Sending...' : 'Resend email'}
              </button>
            </div>
          )}

          {created.warning && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800 flex gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {created.warning}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment link</label>
            <div className="flex gap-2">
              <input readOnly value={created.url} className={inputClass()} onFocus={(e) => e.target.select()} />
              <button
                onClick={async () => {
                  if (await copyToClipboard(created.url)) toast.success('Payment link copied');
                }}
                className="px-4 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
                title="Copy link"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <button
              onClick={() => navigate(`/home/school-payment-links/${created._id}`)}
              className="px-5 py-2.5 bg-[#E55109] text-white rounded-lg hover:bg-[#d14808] text-sm font-medium"
            >
              View link details
            </button>
            <button onClick={resetForm} className="px-5 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700">
              Create another
            </button>
            <button onClick={() => navigate('/home/school-payment-links')} className="px-5 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900">
              Back to all links
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate('/home/school-payment-links')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Back to payment links
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Create Payment Link</h1>
        <p className="text-gray-600 mt-1 mb-6">
          Record the deal agreed with the school. They'll get an email with a Pay Now button.
          A plan subscription switches the plan on once they pay; an onboarding fee is a one-time payment that doesn't change their plan.
        </p>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-md p-6 space-y-6">
          {/* School */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">School *</label>
            {school ? (
              <div className="flex items-center justify-between px-4 py-3 border border-gray-300 rounded-lg bg-gray-50">
                <div>
                  <div className="font-medium text-gray-900">{school.organizationName || school.name || school.appID}</div>
                  <div className="text-sm text-gray-500">{school.appID}{school.email ? ` · ${school.email}` : ''}</div>
                </div>
                <button type="button" onClick={() => setSchool(null)} className="text-gray-400 hover:text-gray-600" title="Change school">
                  <X className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={schoolQuery}
                  onChange={(e) => searchSchools(e.target.value)}
                  onFocus={() => setShowResults(true)}
                  onBlur={() => setTimeout(() => setShowResults(false), 150)}
                  placeholder="Search schools by name or email"
                  className={cn(inputClass(Boolean(errors.school)), "pl-9")}
                />
                {showResults && schoolQuery.trim() && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                    {searchingSchools ? (
                      <div className="px-4 py-3 text-sm text-gray-500 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Searching...
                      </div>
                    ) : schoolResults.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-500">No schools found</div>
                    ) : (
                      schoolResults.map((option) => (
                        <button
                          type="button"
                          key={option._id || option.appID}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setSchool(option);
                            setSchoolQuery('');
                            setShowResults(false);
                            setErrors((prev) => ({ ...prev, school: undefined }));
                          }}
                          className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                        >
                          <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                            {option.organizationName || option.name || option.appID}
                            {option.status && option.status !== 'active' && (
                              <span className="text-xs text-red-600 capitalize">({option.status})</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">{option.appID} · {option.email || 'no email'}</div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
            {errors.school && <p className="text-red-500 text-sm mt-1">{errors.school}</p>}
            <p className="text-xs text-gray-500 mt-1">
              {onboarding
                ? 'Any active school can receive an onboarding fee link.'
                : 'Only active, exclusive K-12 or advance schools can receive a plan subscription link.'}
            </p>
          </div>

          {/* Payment type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment type *</label>
            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
              {(Object.keys(LINK_TYPE_LABELS) as LinkType[]).map((type) => (
                <button
                  type="button"
                  key={type}
                  onClick={() => {
                    setField('linkType', type);
                    setErrors({});
                    setAmountEdited(false);
                  }}
                  className={cn(
                    "flex-1 py-2.5 text-sm transition-colors",
                    form.linkType === type ? "bg-[#E55109] text-white" : "bg-white text-gray-700 hover:bg-gray-50"
                  )}
                >
                  {LINK_TYPE_LABELS[type]}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {onboarding
                ? `A one-time fee of ${onboardingFee ? formatMoney(onboardingFee.perStudent, onboardingFee.currency) : '₦1,000'} per student onboarded. Each student paid for becomes a student slot once the school pays. No plan or billing cycle.`
                : 'Pays for a school plan for one billing cycle and activates it.'}
            </p>
          </div>

          {!onboarding && (
            <>
            {/* Plan */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plan *</label>
              <select
                value={form.planId}
                onChange={(e) => selectPlan(e.target.value)}
                disabled={plansLoading}
                className={cn(inputClass(Boolean(errors.planId)), "bg-white")}
              >
                <option value="">{plansLoading ? 'Loading plans...' : 'Select a plan'}</option>
                {plans.map((plan) => (
                  <option key={plan._id} value={plan._id}>
                    {plan.name} ({plan.minStudents}–{plan.maxStudents ?? '∞'} students)
                  </option>
                ))}
              </select>
              {errors.planId && <p className="text-red-500 text-sm mt-1">{errors.planId}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of students *</label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={form.numberOfStudents}
                  onChange={(e) => setField('numberOfStudents', e.target.value)}
                  placeholder="e.g. 420"
                  className={inputClass(Boolean(errors.numberOfStudents))}
                />
                {errors.numberOfStudents && <p className="text-red-500 text-sm mt-1">{errors.numberOfStudents}</p>}
                <p className="text-xs text-gray-500 mt-1">Becomes the school's seat limit.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Billing cycle *</label>
                <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                  {BILLING_CYCLES.map((cycle) => (
                    <button
                      type="button"
                      key={cycle}
                      onClick={() => setField('billingCycle', cycle)}
                      className={cn(
                        "flex-1 py-2.5 text-sm capitalize transition-colors",
                        form.billingCycle === cycle ? "bg-[#E55109] text-white" : "bg-white text-gray-700 hover:bg-gray-50"
                      )}
                    >
                      {cycle}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {outsideRange && selectedPlan && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 flex gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                {students} students is outside {selectedPlan.name}'s range of {selectedPlan.minStudents} to {selectedPlan.maxStudents ?? 'unlimited'}.
                That's allowed for a negotiated deal; the link will be flagged.
              </div>
            )}
            </>
          )}

          {onboarding ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Students to onboard *</label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={form.numberOfStudents}
                  onChange={(e) => setField('numberOfStudents', e.target.value)}
                  placeholder="e.g. 5"
                  className={inputClass(Boolean(errors.numberOfStudents))}
                />
                {errors.numberOfStudents && <p className="text-red-500 text-sm mt-1">{errors.numberOfStudents}</p>}
                <p className="text-xs text-gray-500 mt-1">The school gets this many student slots once it pays.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Onboarding fee</label>
                <div className="px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-sm font-semibold text-gray-900">
                  {onboardingFee && students > 0
                    ? formatMoney(Math.round(students * onboardingFee.perStudent * 100) / 100, onboardingFee.currency)
                    : '—'}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {onboardingFee
                    ? `${formatMoney(onboardingFee.perStudent, onboardingFee.currency)} × students, paid via Flutterwave. Calculated on save.`
                    : 'Calculated from the per-student rate on save.'}
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency *</label>
                <select
                  value={form.currency}
                  onChange={(e) => setField('currency', e.target.value as LinkCurrency)}
                  className={cn(inputClass(), "bg-white")}
                >
                  <option value="NGN">NGN</option>
                  <option value="USD">USD</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">{form.currency === 'NGN' ? 'Paid via Flutterwave' : 'Paid via Stripe'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {onboarding ? 'Agreed onboarding fee *' : 'Agreed total amount *'}
                </label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => {
                    setAmountEdited(true);
                    setField('amount', e.target.value);
                  }}
                  placeholder="e.g. 1250000"
                  className={inputClass(Boolean(errors.amount))}
                />
                {errors.amount && <p className="text-red-500 text-sm mt-1">{errors.amount}</p>}
                {Number(form.amount) > 0 && !errors.amount && (
                  <p className="text-sm text-gray-700 mt-1 font-medium">{formatMoney(Number(form.amount), form.currency)}</p>
                )}
                {referencePrice !== null && (
                  amountEdited ? (
                    <p className="text-xs text-gray-500 mt-1">
                      Standard checkout price: {formatMoney(referencePrice, form.currency)}{' '}
                      <button type="button" onClick={() => setAmountEdited(false)} className="text-[#E55109] hover:underline">
                        Use this
                      </button>
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">
                      Filled from the plan's standard price. Change it to the amount agreed with the school.
                    </p>
                  )
                )}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Send to email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setField('email', e.target.value)}
              placeholder={school?.email ? `Defaults to ${school.email}` : "Defaults to the school's email"}
              className={inputClass(Boolean(errors.email))}
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>

          {/* Email to the school */}
          <div className="rounded-lg border border-gray-200 p-4 space-y-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Email to the school</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Leave both blank to send the standard email. A message replaces the standard wording and payment details (add them with the placeholders below if you want them); the Pay Now button and the link's expiry date are always added under it.
              </p>
            </div>
            <div>
              <label htmlFor="payment-link-email-subject" className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
              <input
                id="payment-link-email-subject"
                type="text"
                maxLength={200}
                value={form.emailSubject}
                onChange={(e) => setField('emailSubject', e.target.value)}
                placeholder={onboarding ? 'Payment link for your School onboarding fee' : 'Payment link for your {{plan}} school plan'}
                className={inputClass()}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="payment-link-email-message" className="block text-sm font-medium text-gray-700">Message</label>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={boldSelection}
                  title="Bold the selected text"
                  className="px-2.5 py-1 rounded border border-gray-300 bg-white hover:bg-gray-50 text-sm font-bold text-gray-700"
                >
                  B
                </button>
              </div>
              <textarea
                ref={messageRef}
                id="payment-link-email-message"
                value={form.emailMessage}
                maxLength={5000}
                rows={6}
                onChange={(e) => setField('emailMessage', e.target.value)}
                placeholder={"e.g.\nDear {{school}},\n\nThank you for the call today. As agreed, here is the link to pay {{amount}} for {{students}} students. Kindly pay before {{expiry}}."}
                className={inputClass()}
              />
              <p className="text-xs text-gray-500 mt-1">
                Plain text; a blank line starts a new paragraph. Wrap text in **double asterisks** (or select it and press B) to make it bold.
                You can use {'{{school}}'}, {'{{amount}}'}, {'{{students}}'}, {'{{plan}}'} and {'{{expiry}}'}.
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Internal note</label>
            <textarea
              value={form.note}
              maxLength={1000}
              rows={3}
              onChange={(e) => setField('note', e.target.value)}
              placeholder="e.g. Agreed on call, 25 Sep"
              className={inputClass()}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate('/home/school-payment-links')}
              className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-2.5 bg-[#E55109] text-white rounded-lg hover:bg-[#d14808] disabled:opacity-60 text-sm font-medium"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? 'Creating link...' : 'Create and send link'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateSchoolPaymentLinkPage;
